import React, { useState, useCallback, useMemo, cloneElement, useEffect } from 'react';
import { castArray, get, isEmpty, isEqual, pick, set, startCase } from 'lodash';
import classnames from 'classnames';
import {
  Modal,
  Button,
  Upload,
  UploadProps,
  Form,
  Input,
  notification,
  Space,
  Tooltip,
  TooltipProps,
  FormInstance,
  FormItemProps,
  theme,
} from 'antd';
import { EyeOutlined, DeleteOutlined, FileTextOutlined } from '@ant-design/icons';
import { faInbox } from '@fortawesome/free-solid-svg-icons';
import useApi, { UseAPI } from '../../api';
import { Rule } from '../../utils/rules';
import Image from '../Image';
import FaIcon from '../FaIcon';
import './styles.css';

const MAX_UPLOAD_COUNT = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MIMES = {
  IMAGE: {
    types: ['image/png', 'image/jpeg', 'image/gif', 'image/svg+xml'],
    extensions: ['.png', '.jpeg', '.jpg', '.gif', '.svg'],
  },
  VIDEO: {
    types: ['video/mp4'],
    extensions: ['.mp4'],
  },
  CSV: {
    types: ['text/csv', 'application/vnd.ms-excel'],
    extensions: ['.csv'],
  },
  HTML: {
    types: ['text/html', 'text/htm'],
    extensions: ['.html'],
  },
};

export type FileData = {
  id: string;
  url: string;
  dimensions?: { height: number; width?: number };
};

interface FormUploadProps {
  name: NonNullable<FormItemProps['name']>;
  namePrefix?: string;
  /** populated data field */
  dataField?: FormItemProps['name'];
  label?: string;
  multiple?: boolean;
  type: 'IMAGE' | 'VIDEO' | 'CSV' | 'HTML';
  rules?: Rule[];
  disabled?: boolean;
  tooltip?: TooltipProps;
  getForm?: () => FormInstance<any>;
  apiVariables?: any;
  api?: UseAPI;
  fileField?: string;
  isFileArray?: boolean;
  onLoad?: (val: boolean) => void;
  onUpload?: (fileData: FileData | FileData[]) => void;
  trigger?: JSX.Element;
  titleExtra?: JSX.Element;
  uploadProps?: UploadProps;
  beforeUpload?: (file: any) => void;
}

function FormUpload(props: FormUploadProps) {
  const {
    name,
    namePrefix,
    dataField,
    label,
    multiple = false,
    type,
    rules,
    disabled,
    getForm,
    tooltip,
    apiVariables,
    api,
    onUpload,
    onLoad,
    trigger,
    titleExtra,
    uploadProps = {},
    beforeUpload,
  } = props;

  const { token } = theme.useToken();

  const fullName = useMemo(
    () => (namePrefix ? [namePrefix, ...castArray(name)] : name),
    [name, namePrefix],
  );
  const fullDataField = useMemo(
    () => (namePrefix && dataField ? [namePrefix, ...castArray(dataField)] : dataField),
    [dataField, namePrefix],
  );
  const [fileList, setFileList] = useState<any[]>([]);
  const [visible, setVisible] = useState(false);
  const [previewItem, setPreviewItem] = useState<FileData | null>(null);
  const [submitData, { loading }] = api
    ? api({ notification: true })
    : useApi.File.Upload({ form: getForm?.(), notification: true });

  useEffect(() => {
    onLoad?.(loading);
  }, [loading, onLoad]);

  const handleBeforeUpload = useCallback<NonNullable<UploadProps['beforeUpload']>>(
    (file) => {
      if (file.size > MAX_FILE_SIZE) {
        notification.error({
          message: 'File Selection Error',
          description: 'File size exceeds max limit of 5MB',
        });
        return false;
      }
      if (file.type.includes('image')) {
        Object.assign(file, { thumbUrl: URL.createObjectURL(file) });
        beforeUpload?.(file);
      }
      setFileList((oldList) => [...(multiple ? oldList : []), file]);
      return false;
    },
    [beforeUpload, multiple],
  );

  const handleOnRemove = useCallback<NonNullable<UploadProps['onRemove']>>((file) => {
    setFileList((oldList) => {
      const index = oldList.indexOf(file);
      const newFileList = oldList.slice();
      newFileList.splice(index, 1);
      return newFileList;
    });
  }, []);

  const uploadFiles = useCallback(() => {
    const formData = new FormData();
    fileList.forEach((file) => {
      formData.append('files', file);
    });
    Object.keys(apiVariables).forEach((key) => formData.append(key, apiVariables[key]));
    submitData({ data: formData })
      .then((res) => {
        onUpload?.(res);
        if (getForm) {
          if (!res.data?.length) return;
          const { data }: { data: FileData[] } = res;
          const form = getForm();
          let newData: any = namePrefix ? form?.getFieldsValue([namePrefix]) : {};
          if (multiple) {
            const prevIds: string[] = form.getFieldValue(fullName) || [];
            newData = set(newData, fullName, [...prevIds, ...data.map((file) => file.id)]);
            if (fullDataField) {
              const prevData: FileData[] = form.getFieldValue(fullDataField) || [];
              newData = set(newData, fullDataField, [
                ...prevData,
                ...data.map((file) => pick(file, ['id', 'url'])),
              ]);
            }
          }
          else {
            newData = set(newData, fullName, data[0].id);
            if (fullDataField) newData = set(newData, fullDataField, pick(data[0], ['id', 'url']));
          }
          form?.setFieldsValue(newData);
        }
        setVisible(false);
        setFileList([]);
      })
      .catch(() => {});
  }, [
    fileList,
    apiVariables,
    submitData,
    onUpload,
    getForm,
    namePrefix,
    multiple,
    fullName,
    fullDataField,
  ]);

  const handleModalClose = useCallback(() => setVisible(false), []);
  const handleModalOpen = useCallback(() => setVisible(true), []);

  const deleteFile = useCallback(
    (index: number) => {
      if (!getForm) return;
      const form = getForm();
      const value = form?.getFieldValue(fullName);
      let newData: any = set(
        namePrefix ? form?.getFieldsValue([namePrefix]) : {},
        fullName,
        multiple ? value.slice(0, index).concat(value.slice(index + 1)) : null,
      );
      if (fullDataField) {
        const dataFieldVal = form?.getFieldValue(fullDataField);
        newData = set(
          newData,
          fullDataField,
          multiple ? dataFieldVal.slice(0, index).concat(dataFieldVal.slice(index + 1)) : null,
        );
      }
      form?.setFieldsValue(newData);
    },
    [getForm, fullName, namePrefix, multiple, fullDataField],
  );

  const getPreviewItem = (file: FileData, index: number, isLargePreview = false) => {
    return (
      <div key={file.id} className={classnames('preview-item', { large: isLargePreview })}>
        {type === 'IMAGE' ? (
          <Image
            bordered={!isLargePreview}
            src={file.url}
            alt={`${label}_${index + 1}`}
            height={isLargePreview ? 400 : 130}
          />
        ) : null}
        {type === 'CSV' ? (
          <FileTextOutlined
            className="csv-preview"
            style={{ border: `1px solid ${token.colorBorder}`, color: token.colorSuccess }}
          />
        ) : null}
        {!isLargePreview ? (
          <div className="preview-overlay">
            <Tooltip title="Preview">
              <Button
                type="text"
                icon={<EyeOutlined />}
                {...(type === 'CSV' && { href: file.url, target: 'blank' })}
                {...(type === 'IMAGE' && { onClick: () => setPreviewItem(file) })}
              />
            </Tooltip>
            {!disabled ? (
              <Tooltip title="Delete">
                <Button type="text" icon={<DeleteOutlined />} onClick={() => deleteFile(index)} />
              </Tooltip>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  const triggerEl = useMemo(() => {
    if (trigger) {
      return (
        <>
          <Form.Item name={name} label={label} tooltip={tooltip} rules={rules} hidden>
            <Input />
          </Form.Item>
          {cloneElement(trigger, { onClick: handleModalOpen, disabled })}
        </>
      );
    }
    return (
      <div className="upload-form-item">
        <Space size="large">
          <Form.Item
            name={name}
            label={label}
            tooltip={tooltip}
            rules={rules}
            style={{ margin: 0 }}
          >
            <Input type="hidden" />
          </Form.Item>

          <Button type="primary" onClick={handleModalOpen} disabled={disabled}>
            {multiple ? 'Select Files' : 'Select File'}
          </Button>
        </Space>
      </div>
    );
  }, [trigger, name, label, tooltip, rules, handleModalOpen, disabled, multiple]);

  return (
    <>
      {triggerEl}

      {['IMAGE', 'CSV'].includes(type) && fullDataField ? (
        <div className="preview-list">
          <Form.Item
            noStyle
            shouldUpdate={(prevVal, currVal) =>
              !isEqual(get(prevVal, fullName), get(currVal, fullName))
            }
          >
            {({ getFieldValue }) => {
              const files = getFieldValue(fullDataField);
              const fileArr: FileData[] = files && !isEmpty(files) ? castArray(files) : [];
              return fileArr.map((file, i) => getPreviewItem(file, i));
            }}
          </Form.Item>
        </div>
      ) : null}

      <Modal
        title="Preview"
        open={!!previewItem}
        width={800}
        footer={null}
        onCancel={() => setPreviewItem(null)}
      >
        {previewItem && getPreviewItem(previewItem, -1, true)}
      </Modal>

      <Modal
        title={
          <div>
            <span>Upload {startCase(type.toLowerCase())}</span>
            {titleExtra || null}
          </div>
        }
        open={visible}
        onCancel={handleModalClose}
        okText="Upload"
        onOk={uploadFiles}
        confirmLoading={loading}
        okButtonProps={{ disabled: !fileList.length }}
        width={600}
        cancelButtonProps={{ disabled: true }}
        wrapClassName="upload-modal-wrapper"
      >
        <Upload.Dragger
          multiple={multiple}
          fileList={fileList}
          maxCount={MAX_UPLOAD_COUNT}
          beforeUpload={handleBeforeUpload}
          onRemove={handleOnRemove}
          listType={uploadProps.listType || 'picture'}
          accept={MIMES[type].types.join(',')}
          {...uploadProps}
        >
          <p className="ant-upload-drag-icon">
            <FaIcon icon={faInbox} size="lg" />
          </p>
          <p className="ant-upload-text">Click or drag file to this area</p>
          <p className="ant-upload-hint">
            {multiple && (
              <>
                (Max 5 Files can be uploaded once)
                <br />
              </>
            )}
            Supported Types:
            {MIMES[type].extensions.join(', ')}
            <br />
            Max Size: 5MB
          </p>
        </Upload.Dragger>
      </Modal>
    </>
  );
}

export default FormUpload;
